import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import WebvonkLogoImage from '../assets/Webvonk.png';
import { useTheme } from '../contexts/ThemeContext';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-8px); }
  40%       { transform: translateX(8px); }
  60%       { transform: translateX(-6px); }
  80%       { transform: translateX(6px); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${props => props.theme.colors.background};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  animation: ${fadeIn} 0.2s ease;
  gap: 32px;
`;

const Logo = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  filter: ${props => props.theme.mode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)'};
  opacity: 0.8;
`;

const Title = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  letter-spacing: -0.02em;
  opacity: 0.7;
`;

const Dots = styled.div`
  display: flex;
  gap: 16px;
  animation: ${props => props.$shake ? shakeAnim : 'none'} 0.4s ease;
`;

const Dot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid ${props => props.$filled ? '#f97316' : props.theme.mode === 'light' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)'};
  background: ${props => props.$filled ? '#f97316' : 'transparent'};
  transition: all 0.15s ease;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 72px);
  grid-template-rows: repeat(4, 72px);
  gap: 10px;
`;

const Key = styled.button`
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'};
  border: 1px solid ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  border-radius: 50%;
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.$label ? '16px' : '22px'};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: background 0.1s, transform 0.1s;
  letter-spacing: -0.01em;
  line-height: 1;
  gap: 2px;

  &:active {
    background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)'};
    transform: scale(0.93);
  }

  ${props => props.$empty && 'pointer-events: none; background: transparent; border-color: transparent;'}
  ${props => props.$delete && 'background: transparent; border-color: transparent; font-size: 20px;'}
`;

const Sub = styled.div`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.35;
`;

const KEYS = [
  { digit: '1', sub: '' },
  { digit: '2', sub: 'ABC' },
  { digit: '3', sub: 'DEF' },
  { digit: '4', sub: 'GHI' },
  { digit: '5', sub: 'JKL' },
  { digit: '6', sub: 'MNO' },
  { digit: '7', sub: 'PQRS' },
  { digit: '8', sub: 'TUV' },
  { digit: '9', sub: 'WXYZ' },
  { digit: null },
  { digit: '0', sub: '' },
  { digit: 'del' },
];

const PIN_LENGTH = 6;

export default function PinLockScreen({ onUnlock }) {
  const { colors } = useTheme();
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');
  const [lockedUntil, setLockedUntil] = useState(0);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!lockedUntil) return;
    const tick = () => {
      const left = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (left <= 0) { setLockedUntil(0); setCountdown(0); setMessage(''); }
      else setCountdown(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const isLocked = lockedUntil > Date.now();

  const press = (digit) => {
    if (checking || isLocked) return;
    setPin(prev => prev.length < PIN_LENGTH ? prev + digit : prev);
  };

  const del = () => { if (!isLocked) setPin(prev => prev.slice(0, -1)); };

  useEffect(() => {
    if (pin.length === PIN_LENGTH) {
      setChecking(true);
      onUnlock(pin).then(result => {
        if (result && result.ok) return;
        setShake(true);
        if (result && result.lockedUntil) {
          setLockedUntil(result.lockedUntil);
          setMessage('Te veel pogingen');
        } else if (result && result.remaining !== undefined) {
          setMessage(`Onjuist — nog ${result.remaining} ${result.remaining === 1 ? 'poging' : 'pogingen'}`);
        } else {
          setMessage('Onjuiste pincode');
        }
        setTimeout(() => { setShake(false); setPin(''); setChecking(false); }, 600);
      });
    }
  }, [pin, onUnlock]);

  useEffect(() => {
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') press(e.key);
      if (e.key === 'Backspace') del();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [checking, isLocked]);

  return ReactDOM.createPortal(
    <Overlay>
      <Logo src={WebvonkLogoImage} alt="Webvonk" />
      <Title>Voer je pincode in</Title>
      <Dots $shake={shake}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <Dot key={i} $filled={i < pin.length} />
        ))}
      </Dots>
      {isLocked ? (
        <div style={{ fontSize: 13, color: '#ef4444', fontWeight: 500, textAlign: 'center', lineHeight: 1.5 }}>
          Te veel pogingen<br />
          <span style={{ fontSize: 12, color: colors.muted }}>Probeer opnieuw over {countdown}s</span>
        </div>
      ) : message ? (
        <div style={{ fontSize: 13, color: '#ef4444', fontWeight: 500 }}>{message}</div>
      ) : (
        <div style={{ height: 20 }} />
      )}
      <Grid style={{ opacity: isLocked ? 0.3 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
        {KEYS.map(({ digit, sub }, i) => (
          digit === null ? (
            <Key key={i} $empty />
          ) : digit === 'del' ? (
            <Key key={i} $delete onClick={del}>⌫</Key>
          ) : (
            <Key key={i} onClick={() => press(digit)}>
              {digit}
              {sub && <Sub>{sub}</Sub>}
            </Key>
          )
        ))}
      </Grid>
    </Overlay>,
    document.body
  );
}
